/**
 * Card classification cache state — Derived atom caching CardClassification by oracle_id
 *
 * Classifications are identical across printings of the same card, so we deduplicate
 * by oracle_id. The cache is derived from fullCardCacheAtom and automatically
 * updates when card data changes.
 *
 * Replaces the old cardRolesByOracleIdAtom (state/cardRoles.ts) with full
 * CardClassification objects instead of just roles.
 *
 * Shared across Collection, Deck Editor, and Lists views.
 */
import { atom } from 'jotai';

import { classifyCard } from '@/lib/cardClassification';
import { getOracleId } from '@/lib/cardUtils';
import { fullCardCacheAtom } from '@/state/cardCache';
import type { CardClassification } from '@/types/card';

// ============================================================================
// Types
// ============================================================================

/** Map from oracle_id → cached full CardClassification */
export type CardClassificationCache = Map<string, CardClassification>;

// ============================================================================
// Atoms
// ============================================================================

/**
 * Derived atom: CardClassification indexed by oracle_id.
 *
 * Iterates fullCardCacheAtom once, classifies each card via classifyCard(),
 * and deduplicates by oracle_id (classification is identical across printings).
 */
export const cardClassificationByOracleIdAtom = atom<CardClassificationCache>((get) => {
  const cache = get(fullCardCacheAtom);
  const classificationMap: CardClassificationCache = new Map();

  for (const card of cache.values()) {
    const oracleId = getOracleId(card);
    // Skip cards with no oracle_id and already-classified
    if (!oracleId || classificationMap.has(oracleId)) continue;

    classificationMap.set(oracleId, classifyCard(card));
  }

  return classificationMap;
});

// ============================================================================
// Helpers
// ============================================================================

/**
 * Get the CardClassification for a card from the cache.
 *
 * @param cache - The CardClassificationCache from cardClassificationByOracleIdAtom
 * @param oracleId - The card's oracle_id
 * @returns CardClassification or undefined if not in cache
 */
export function getClassificationForCard(cache: CardClassificationCache, oracleId: string | null): CardClassification | undefined {
  if (!oracleId) return undefined;
  return cache.get(oracleId);
}
